import * as React from "react";
import { getStoredPassword } from "./core.js";

export function ProtectedVideo({
  comicId,
  title,
  poster,
  enabled,
  hasFullAccess,
  onLockedClick,
  onUnauthorized,
}) {
  const [requested, setRequested] = React.useState(false);
  const [state, setState] = React.useState("idle");
  const [attempt, setAttempt] = React.useState(0);
  const callbacks = React.useRef({ onUnauthorized });
  callbacks.current = { onUnauthorized };

  React.useEffect(() => {
    if (!requested || !enabled || !hasFullAccess) {
      setState("idle");
      return;
    }
    const controller = new AbortController();
    setState("loading");
    (async () => {
      try {
        const response = await fetch(
          `/api/video-session?comicId=${encodeURIComponent(comicId)}`,
          {
            method: "POST",
            credentials: "same-origin",
            headers: { "X-Comic-Password": getStoredPassword() || "" },
            signal: controller.signal,
          },
        );
        if (controller.signal.aborted) return;
        if (response.status === 401) {
          setState("idle");
          callbacks.current.onUnauthorized();
          return;
        }
        if (response.status !== 204)
          throw new Error("Video session unavailable");
        setState("ready");
      } catch {
        if (!controller.signal.aborted) setState("error");
      }
    })();
    return () => controller.abort();
  }, [comicId, enabled, hasFullAccess, requested, attempt]);

  const start = () => {
    setRequested(true);
    if (!hasFullAccess) onLockedClick();
    else setAttempt((value) => value + 1);
  };
  return (
    <div className="protected-video video-preview-media" data-video-player>
      {state === "ready" && enabled && hasFullAccess ? (
        <video
          key={attempt}
          src={`/api/comic-video?comicId=${encodeURIComponent(comicId)}`}
          poster={poster}
          controls
          playsInline
          autoPlay
          preload="metadata"
          aria-label={`Video: ${title}`}
          onError={() => setState("error")}
          onPlay={(event) => {
            document.querySelectorAll("video").forEach((other) => {
              if (other !== event.currentTarget) other.pause();
            });
          }}
        />
      ) : (
        <div className="video-preview-unavailable">
          <img src={poster} alt="" loading="lazy" />
          <div className="video-preview-message">
            {state === "loading" ? (
              <span role="status">Preparing your video…</span>
            ) : (
              <>
                {state === "error" && (
                  <span role="status">
                    Unable to play the video. Please try again.
                  </span>
                )}
                <button
                  type="button"
                  className="button"
                  onClick={start}
                  disabled={!enabled}
                >
                  {state === "error"
                    ? "Try again"
                    : hasFullAccess
                      ? "Play video"
                      : "Unlock video"}
                </button>
                {!hasFullAccess && <span>Use your comic reader password.</span>}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
